import { type FC, useCallback } from "react";
import { Form } from "react-bootstrap";
import type { ReferenceMode } from "shared";
import { useModal } from "../../common/modal/Modal";
import { Button } from "../../components/Button";
import { ModalSelectMedia } from "../../post/ModalSelectMedia";
import { useSuspenseMedia, useSuspenseMediaList } from "../useMediaBatch";

interface Props {
	selectedId: number | undefined;
	mode: ReferenceMode | undefined;
	onSelectId: (id: number | undefined) => void;
	onModeChange: (mode: ReferenceMode) => void;
}

export const ReferenceImageSelector: FC<Props> = ({
	selectedId,
	mode,
	onSelectId,
	onModeChange,
}) => {
	const { open } = useModal();
	const ids = useSuspenseMediaList();
	const media = useSuspenseMedia(ids);

	const selectedMedia = selectedId
		? media.find((m) => m.id === selectedId)
		: undefined;

	const handleSelectClick = useCallback(() => {
		open(ModalSelectMedia, {
			values: selectedId ? [selectedId] : [],
			multiple: false,
			onChange: (ids: number[]) => {
				onSelectId(ids[0]);
			},
		});
	}, [open, selectedId, onSelectId]);

	const handleClear = useCallback(() => {
		onSelectId(undefined);
	}, [onSelectId]);

	return (
		<div className="reference-image-selector">
			<div className="d-flex gap-2 align-items-center mb-2">
				<Button
					variant="ghost"
					size="sm"
					icon="photo_library"
					onClick={handleSelectClick}
				>
					{selectedMedia ? "Change reference image" : "Select reference image"}
				</Button>
				{selectedMedia && (
					<Button variant="link" size="sm" onClick={handleClear}>
						Clear
					</Button>
				)}
			</div>

			{selectedMedia && (
				<div className="mb-3">
					<div className="reference-image-preview mb-2">
						<img
							src={selectedMedia.thumbnails["256"] || selectedMedia.url}
							alt={selectedMedia.filename}
							style={{
								maxWidth: "120px",
								maxHeight: "120px",
								objectFit: "cover",
								borderRadius: "8px",
							}}
						/>
					</div>
					<div className="d-flex gap-3">
						<Form.Check
							type="radio"
							id="mode-inspiration"
							name="referenceMode"
							label="Use as inspiration"
							checked={mode === "inspiration"}
							onChange={() => onModeChange("inspiration")}
						/>
						<Form.Check
							type="radio"
							id="mode-base"
							name="referenceMode"
							label="Use as base image"
							checked={mode === "base"}
							onChange={() => onModeChange("base")}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default ReferenceImageSelector;
